/**
 * RegistrationTour-specific types and step definitions.
 *
 * The RegistrationTour walks users through the 10DLC and phone number
 * registration process — explaining the Complete Registration flow,
 * individual registration paths (new, host, port), business
 * verification, and compliance resources.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each RegistrationTour step */
export type RegistrationTourStepId =
  | 'registration_welcome'
  | 'highlight_page_header'
  | 'highlight_submit_btn'
  | 'highlight_approved_tab'
  | 'highlight_register_tab'
  | 'highlight_10dlc_tab'
  | 'registration_complete';

/** Ordered list of all RegistrationTour steps */
export const REGISTRATION_TOUR_STEPS: TourStep[] = [
  {
    id: 'registration_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_page_header',
    type: 'highlight',
    targetSelector: '[data-tour-id="registration-header"]',
    title: 'Phone Number Registration',
    content:
      'This is your registration hub. Before you can send SMS messages through Voley, you need two things: a registered phone number and 10DLC business verification. Voley handles the entire process for you — just provide your information and we take care of the rest.',
    placement: 'bottom',
  },
  {
    id: 'highlight_submit_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="submit-registration-btn"]',
    title: 'Complete Registration',
    content:
      'This is the fastest way to get started. The Complete Registration flow walks you through both steps in one sitting — first you\'ll choose your phone number (purchase a new one, host an existing number, or port from another carrier), then you\'ll provide your business information for 10DLC verification. The entire process takes about 5 minutes.',
    placement: 'bottom',
  },
  {
    id: 'highlight_approved_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="tab-phone-numbers"]',
    title: 'Approved Numbers',
    content:
      'Once your phone number and 10DLC application are both approved, your number will appear here. Approved numbers are fully activated and ready to send messages. Your messaging dashboard will automatically unlock when your first number is approved.',
    placement: 'bottom',
  },
  {
    id: 'highlight_register_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="tab-register-number"]',
    title: 'Number Registrations',
    content:
      'This tab tracks all your phone number registration requests and their status. You can also submit individual number registrations here if you\'ve already completed 10DLC verification separately. You have three options: purchase a new number ($5), host an existing landline ($20), or port a number from another carrier ($20).',
    placement: 'bottom',
  },
  {
    id: 'highlight_10dlc_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="tab-10dlc"]',
    title: '10DLC Business Verification',
    content:
      '10DLC (10-Digit Long Code) is a carrier-mandated registration that verifies your business identity for SMS messaging. You\'ll provide your legal business name, EIN, industry, and address. Carriers typically review applications within 5–10 business days. The one-time application fee is $10.',
    placement: 'bottom',
  },
  {
    id: 'registration_complete',
    type: 'modal',
    title: '',
    content: '',
  },
];
